import React from 'react'
import { Box, Grid, Text } from '@mantine/core'
import LabeledTitle from '../../../components/LabeledTitle'
import { CompanyItem } from './types'
import ItemWrapper from '../../../components/ItemWrapper'

interface CompanySummaryProps {
  companies: CompanyItem[]
}

const CompanySummary: React.FC<CompanySummaryProps> = ({ companies }) => {
  const totalCount = companies.length
  const activeCount = companies.filter((c) => c.basicInfo.regStatus === '存续').length
  const relationshipTypes = Array.from(new Set(companies.flatMap((c) => c.relationship))).length
  const regions = Array.from(new Set(companies.map((c) => c.basicInfo.base).filter(Boolean))).length

  const hasStockholderInfo = companies.filter((c) => c.stockholder).length
  const averageInvestRate =
    companies
      .filter((c) => c.stockholder?.investRate)
      .reduce((sum, c) => {
        const rate = parseFloat(c.stockholder.investRate.replace('%', ''))
        return sum + (isNaN(rate) ? 0 : rate)
      }, 0) / Math.max(hasStockholderInfo, 1)

  const dishonestCount = companies.reduce((sum, c) => sum + (c.dishonestExecutedPerson?.length || 0), 0)
  const executedCount = companies.reduce((sum, c) => sum + (c.executedPerson?.length || 0), 0)
  const penaltyCount = companies.reduce((sum, c) => sum + (c.adminPenalty?.length || 0), 0)

  const cardBackgroundColor = '#ffffff'

  return (
    <ItemWrapper>
      <LabeledTitle title="关联企业概览" />
      <Grid gutter="md">
        <Grid.Col span={3}>
          <Box
            ta="center"
            p="md"
            style={{
              backgroundColor: cardBackgroundColor,
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}
          >
            <Text size="xl" fw={700} style={{ color: '#1c7ed6' }}>
              {totalCount}
            </Text>
            <Text size="sm" c="dimmed">
              关联企业总数
            </Text>
          </Box>
        </Grid.Col>
        <Grid.Col span={3}>
          <Box
            ta="center"
            p="md"
            style={{
              backgroundColor: cardBackgroundColor,
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}
          >
            <Text size="xl" fw={700} style={{ color: '#22c55e' }}>
              {activeCount}
            </Text>
            <Text size="sm" c="dimmed">
              存续企业数
            </Text>
          </Box>
        </Grid.Col>
        <Grid.Col span={3}>
          <Box
            ta="center"
            p="md"
            style={{
              backgroundColor: cardBackgroundColor,
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}
          >
            <Text size="xl" fw={700} style={{ color: '#a855f7' }}>
              {relationshipTypes}
            </Text>
            <Text size="sm" c="dimmed">
              关联类型数
            </Text>
          </Box>
        </Grid.Col>
        <Grid.Col span={3}>
          <Box
            ta="center"
            p="md"
            style={{
              backgroundColor: cardBackgroundColor,
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}
          >
            <Text size="xl" fw={700} style={{ color: '#f97316' }}>
              {regions}
            </Text>
            <Text size="sm" c="dimmed">
              涉及地区数
            </Text>
          </Box>
        </Grid.Col>
        {hasStockholderInfo > 0 && (
          <>
            <Grid.Col span={6}>
              <Box
                ta="center"
                p="md"
                style={{
                  backgroundColor: cardBackgroundColor,
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                }}
              >
                <Text size="xl" fw={700} style={{ color: '#06b6d4' }}>
                  {hasStockholderInfo}
                </Text>
                <Text size="sm" c="dimmed">
                  有持股信息企业
                </Text>
              </Box>
            </Grid.Col>
            <Grid.Col span={6}>
              <Box
                ta="center"
                p="md"
                style={{
                  backgroundColor: cardBackgroundColor,
                  borderRadius: '8px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                }}
              >
                <Text size="xl" fw={700} style={{ color: '#8b5cf6' }}>
                  {averageInvestRate.toFixed(2)}%
                </Text>
                <Text size="sm" c="dimmed">
                  平均持股比例
                </Text>
              </Box>
            </Grid.Col>
          </>
        )}
        <Grid.Col span={4}>
          <Box
            ta="center"
            p="md"
            style={{
              backgroundColor: cardBackgroundColor,
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}
          >
            <Text size="xl" fw={700} style={{ color: dishonestCount > 0 ? '#ef4444' : '#22c55e' }}>
              {dishonestCount}
            </Text>
            <Text size="sm" c="dimmed">
              失信案件数
            </Text>
          </Box>
        </Grid.Col>
        <Grid.Col span={4}>
          <Box
            ta="center"
            p="md"
            style={{
              backgroundColor: cardBackgroundColor,
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}
          >
            <Text size="xl" fw={700} style={{ color: executedCount > 0 ? '#f97316' : '#22c55e' }}>
              {executedCount}
            </Text>
            <Text size="sm" c="dimmed">
              被执行案件数
            </Text>
          </Box>
        </Grid.Col>
        <Grid.Col span={4}>
          <Box
            ta="center"
            p="md"
            style={{
              backgroundColor: cardBackgroundColor,
              borderRadius: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            }}
          >
            <Text size="xl" fw={700} style={{ color: penaltyCount > 0 ? '#f59e0b' : '#22c55e' }}>
              {penaltyCount}
            </Text>
            <Text size="sm" c="dimmed">
              行政处罚数
            </Text>
          </Box>
        </Grid.Col>
      </Grid>
    </ItemWrapper>
  )
}

export default CompanySummary
